/**
 * PNG metadata chunk reader + writer.
 *
 * Phase 2.B.1 goal: read AI-related text chunks (tEXt, iTXt) from
 * imported PNG images and re-emit them verbatim into the export PNG so
 * provenance survives the round-trip. Photoshop / Lightroom / generic
 * editors strip these chunks; BLRRD's differentiator is that they
 * don't.
 *
 * Codex 2.B.1.design HIGH notes locked in:
 *   - HIGH-1: preserve RAW chunk BYTES (type + length + data + CRC),
 *     never re-encode as fresh tEXt — would lose iTXt non-ASCII data
 *     and ComfyUI workflow JSON nuances.
 *   - HIGH-3: scope v1 to tEXt and iTXt only. zTXt (compressed) is
 *     ignorable in v1; if present we just skip it (no failure).
 *   - HIGH-4: export splices AI chunks BEFORE the IEND marker. Pixel
 *     chunks (IHDR / IDAT / PLTE / etc.) are untouched.
 *
 * Pure ESM, no dependencies. Used by ai-metadata-extract.js for
 * vendor detection and by the editor's export pipeline for splice.
 *
 * @module src/pipeline/png-metadata
 */

const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]);

/* CRC-32 IEEE 802.3 (PNG spec). Table generated once on first use. */
let crcTable = null;
function buildCrcTable() {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        }
        t[n] = c >>> 0;
    }
    return t;
}
function crc32(bytes, start, end) {
    if (!crcTable) crcTable = buildCrcTable();
    let c = 0xFFFFFFFF;
    for (let i = start; i < end; i++) {
        c = crcTable[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    }
    return (c ^ 0xFFFFFFFF) >>> 0;
}

/* Big-endian 32-bit read from a Uint8Array. */
function readU32(buf, offset) {
    return ((buf[offset]     << 24)
         |  (buf[offset + 1] << 16)
         |  (buf[offset + 2] <<  8)
         |   buf[offset + 3]) >>> 0;
}
function writeU32(buf, offset, value) {
    buf[offset]     = (value >>> 24) & 0xFF;
    buf[offset + 1] = (value >>> 16) & 0xFF;
    buf[offset + 2] = (value >>>  8) & 0xFF;
    buf[offset + 3] =  value         & 0xFF;
}

/* Decode a 4-byte chunk type into its ASCII string. */
function readChunkType(buf, offset) {
    return String.fromCharCode(
        buf[offset], buf[offset + 1], buf[offset + 2], buf[offset + 3],
    );
}

/**
 * Verify that `bytes` starts with the PNG file signature.
 *
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isPNG(bytes) {
    if (!bytes || bytes.length < PNG_SIGNATURE.length) return false;
    for (let i = 0; i < PNG_SIGNATURE.length; i++) {
        if (bytes[i] !== PNG_SIGNATURE[i]) return false;
    }
    return true;
}

/**
 * Iterate every chunk in a PNG byte buffer. Each yielded entry exposes:
 *   - `type`     four-letter chunk type (e.g. 'IHDR', 'tEXt', 'IDAT')
 *   - `data`     Uint8Array view of the chunk's payload (no length/crc)
 *   - `start`    absolute byte offset of the 4-byte length field
 *   - `end`      absolute byte offset just past the CRC (exclusive)
 *   - `raw`      Uint8Array view spanning [start, end) — exact original
 *                bytes (length + type + data + crc) for raw re-emission
 *
 * Returns an array, not a generator, so callers can map/filter without
 * worrying about iterator semantics. Stops at IEND.
 *
 * @param {Uint8Array} bytes
 * @returns {Array<{ type: string, data: Uint8Array, start: number, end: number, raw: Uint8Array }>}
 */
export function listChunks(bytes) {
    if (!isPNG(bytes)) {
        throw new Error('listChunks: not a PNG (missing signature)');
    }
    const out = [];
    let p = PNG_SIGNATURE.length;
    while (p + 12 <= bytes.length) {
        const length = readU32(bytes, p);
        const typeOffset = p + 4;
        const type = readChunkType(bytes, typeOffset);
        const dataOffset = p + 8;
        const crcOffset = dataOffset + length;
        if (crcOffset + 4 > bytes.length) break;       /* truncated */
        const end = crcOffset + 4;
        out.push({
            type,
            data: bytes.subarray(dataOffset, dataOffset + length),
            start: p,
            end,
            raw: bytes.subarray(p, end),
        });
        p = end;
        if (type === 'IEND') break;
    }
    return out;
}

/**
 * Parse a tEXt chunk payload into { keyword, text }. tEXt is keyword +
 * null + Latin-1 text. Returns null if the payload is malformed.
 *
 * @param {Uint8Array} data
 * @returns {{ keyword: string, text: string } | null}
 */
export function parseTEXt(data) {
    if (!data || data.length < 2) return null;
    const nullIdx = data.indexOf(0);
    if (nullIdx < 1 || nullIdx > 79) return null;
    /* Latin-1 → JS string. PNG spec mandates Latin-1 here. */
    let keyword = '';
    for (let i = 0; i < nullIdx; i++) keyword += String.fromCharCode(data[i]);
    let text = '';
    for (let i = nullIdx + 1; i < data.length; i++) text += String.fromCharCode(data[i]);
    return { keyword, text };
}

/**
 * Parse an iTXt chunk payload into { keyword, compressed, languageTag,
 * translatedKeyword, text }. iTXt is keyword + null + compression flag
 * (0/1) + compression method (0) + language tag + null + translated
 * keyword (UTF-8) + null + text (UTF-8, possibly compressed via zlib).
 *
 * Returns null if the payload is malformed. We do NOT attempt to
 * decompress compressed payloads in v1 — `compressed: true` callers can
 * decide what to do. Uncompressed payloads are decoded as UTF-8.
 *
 * @param {Uint8Array} data
 * @returns {{ keyword: string, compressed: boolean, languageTag: string,
 *             translatedKeyword: string, text: string } | null}
 */
export function parseITXt(data) {
    if (!data || data.length < 6) return null;
    const k0 = data.indexOf(0);
    if (k0 < 1 || k0 > 79) return null;
    /* Latin-1 keyword. */
    let keyword = '';
    for (let i = 0; i < k0; i++) keyword += String.fromCharCode(data[i]);
    if (k0 + 2 >= data.length) return null;
    const compressionFlag   = data[k0 + 1];
    const compressionMethod = data[k0 + 2];
    /* Compression flag must be 0 or 1; method must be 0 (zlib). */
    if (compressionFlag !== 0 && compressionFlag !== 1) return null;
    if (compressionMethod !== 0) return null;
    /* Language tag (ISO-646 / ASCII), null-terminated. */
    let p = k0 + 3;
    const langEnd = data.indexOf(0, p);
    if (langEnd < 0) return null;
    let languageTag = '';
    for (let i = p; i < langEnd; i++) languageTag += String.fromCharCode(data[i]);
    p = langEnd + 1;
    const tEnd = data.indexOf(0, p);
    if (tEnd < 0) return null;
    /* Translated keyword: UTF-8. */
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const translatedKeyword = decoder.decode(data.subarray(p, tEnd));
    p = tEnd + 1;
    let text = '';
    if (compressionFlag === 0) {
        text = decoder.decode(data.subarray(p));
    } else {
        /* Compressed text payload — return the raw bytes as a hex string
         * indicator. v1 callers don't need to decode this; the raw
         * chunk is preserved through export verbatim. */
        text = ''; /* opaque to v1 */
    }
    return {
        keyword,
        compressed: compressionFlag === 1,
        languageTag,
        translatedKeyword,
        text,
    };
}

/**
 * Filter `listChunks` output to the chunks BLRRD considers "AI-related"
 * for round-trip preservation. Currently: tEXt and iTXt where the
 * keyword matches a known AI-tool key (case-sensitive per PNG spec
 * note: keys like "parameters" are exact). Returns the same array of
 * chunk descriptors so callers can splice raw bytes on export.
 *
 * @param {Array<{ type: string, data: Uint8Array }>} chunks
 * @returns {Array<{ type: string, data: Uint8Array, raw: Uint8Array, keyword: string }>}
 */
export function filterAIChunks(chunks) {
    const out = [];
    for (const ch of chunks) {
        if (ch.type !== 'tEXt' && ch.type !== 'iTXt') continue;
        let keyword = null;
        if (ch.type === 'tEXt') {
            const p = parseTEXt(ch.data);
            keyword = p ? p.keyword : null;
        } else {
            const p = parseITXt(ch.data);
            keyword = p ? p.keyword : null;
        }
        if (!keyword) continue;
        if (!isAIKeyword(keyword)) continue;
        out.push({ ...ch, keyword });
    }
    return out;
}

/* Known AI-tool metadata keywords (case-sensitive per spec convention).
 * Add more as new tools surface in the wild. */
const AI_KEYWORDS = new Set([
    'parameters',         /* A1111 / Forge */
    'prompt',             /* ComfyUI */
    'workflow',           /* ComfyUI */
    'Negative prompt',    /* A1111 long-form */
    'Description',        /* MJ embedded */
    'Software',           /* generic AI generator marker */
    'Comment',            /* Diffusion-related libraries */
    'Title',              /* MJ uses this sometimes */
    'Author',             /* generic */
]);
function isAIKeyword(keyword) {
    return AI_KEYWORDS.has(keyword);
}

/**
 * Compose a new PNG byte buffer that re-emits the original PNG plus a
 * supplied set of raw chunk slices, spliced before IEND.
 *
 * Use case: export pipeline encodes the edited canvas as a fresh PNG
 * (no AI metadata), then calls splicePNGChunks(encoded, savedAIChunks)
 * to inject the preserved chunks. Pixel chunks are NOT touched — we
 * only insert before IEND.
 *
 * If `pngBytes` lacks IEND or any AI chunk has the wrong CRC, the
 * function throws (export caller should fall back to non-spliced PNG).
 *
 * @param {Uint8Array} pngBytes        freshly-encoded PNG output
 * @param {Array<{ raw: Uint8Array }>} aiChunks  raw chunk byte slices
 * @returns {Uint8Array}               new PNG with AI chunks spliced in
 */
export function splicePNGChunks(pngBytes, aiChunks) {
    if (!isPNG(pngBytes)) {
        throw new Error('splicePNGChunks: target is not a PNG');
    }
    if (!aiChunks || aiChunks.length === 0) {
        /* Nothing to splice — return original. */
        return pngBytes;
    }
    /* Locate IEND. */
    const chunks = listChunks(pngBytes);
    const iend = chunks[chunks.length - 1];
    if (!iend || iend.type !== 'IEND') {
        throw new Error('splicePNGChunks: no IEND chunk');
    }
    /* Validate each AI chunk's stored CRC matches the recomputed CRC
     * (defends against accidental corruption when slicing came from a
     * mangled source buffer). */
    for (const c of aiChunks) {
        const r = c.raw;
        if (!r || r.length < 12) {
            throw new Error('splicePNGChunks: invalid raw chunk slice');
        }
        const length = readU32(r, 0);
        if (r.length !== length + 12) {
            throw new Error('splicePNGChunks: raw chunk length mismatch');
        }
        const expectedCrc = readU32(r, 8 + length);
        const actualCrc = crc32(r, 4, 8 + length);   /* CRC over type + data */
        if (expectedCrc !== actualCrc) {
            throw new Error('splicePNGChunks: AI chunk CRC mismatch');
        }
    }
    /* Concatenate: bytes-before-IEND + AI chunks + IEND + bytes-after-IEND. */
    const beforeIend = pngBytes.subarray(0, iend.start);
    const iendChunk  = pngBytes.subarray(iend.start, iend.end);
    const afterIend  = pngBytes.subarray(iend.end);
    let totalLen = beforeIend.length + iendChunk.length + afterIend.length;
    for (const c of aiChunks) totalLen += c.raw.length;
    const out = new Uint8Array(totalLen);
    let p = 0;
    out.set(beforeIend, p); p += beforeIend.length;
    for (const c of aiChunks) { out.set(c.raw, p); p += c.raw.length; }
    out.set(iendChunk, p); p += iendChunk.length;
    out.set(afterIend, p); /* trailing junk if any (rare) */
    return out;
}

/**
 * Build a fresh tEXt chunk byte slice (length + type + data + CRC).
 * Helper for tests and tooling, not used by the main read-preserve
 * loop. Keyword must be 1..79 ASCII chars; text is Latin-1.
 *
 * @param {string} keyword
 * @param {string} text
 * @returns {Uint8Array}
 */
export function buildTEXt(keyword, text) {
    if (!keyword || keyword.length < 1 || keyword.length > 79) {
        throw new Error('buildTEXt: keyword must be 1..79 chars');
    }
    const data = new Uint8Array(keyword.length + 1 + text.length);
    for (let i = 0; i < keyword.length; i++) data[i] = keyword.charCodeAt(i) & 0xFF;
    data[keyword.length] = 0;
    for (let i = 0; i < text.length; i++) data[keyword.length + 1 + i] = text.charCodeAt(i) & 0xFF;
    const length = data.length;
    const out = new Uint8Array(12 + length);
    writeU32(out, 0, length);
    out[4] = 0x74; out[5] = 0x45; out[6] = 0x58; out[7] = 0x74;   /* 'tEXt' */
    out.set(data, 8);
    const crc = crc32(out, 4, 8 + length);
    writeU32(out, 8 + length, crc);
    return out;
}

/* Re-export helpers for tests. */
export { crc32, readU32, writeU32, PNG_SIGNATURE };
